import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSend, FiTrash2, FiMessageCircle } from 'react-icons/fi';
import { format } from 'date-fns';

const TaskComments = ({ taskId }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [taskId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/comments/task/${taskId}`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await api.post('/comments', {
        task_id: taskId,
        comment: newComment.trim()
      });
      setComments([...comments, response.data.comment]);
      setNewComment('');
      showSuccess('Comment added successfully!', 'Comment Added');
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to add comment', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await api.delete(`/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      showSuccess('Comment deleted successfully!', 'Comment Deleted');
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to delete comment', 'Error');
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center space-x-2 mb-4">
        <FiMessageCircle className="text-white/80" size={20} />
        <h3 className="text-white font-semibold text-lg">Comments ({comments.length})</h3>
      </div>

      {/* Comments List */}
      <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
        <AnimatePresence>
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto"></div>
            </div>
          ) : comments.length === 0 ? (
            <p className="text-white/60 text-center py-4">No comments yet. Be the first to comment!</p>
          ) : (
            comments.map((comment) => (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-white/5 rounded-lg p-3 border border-white/10"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-white font-medium text-sm">{comment.user_name}</span>
                      <span className="text-white/40 text-xs">
                        {format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}
                      </span>
                    </div>
                    <p className="text-white/80 text-sm whitespace-pre-wrap">{comment.comment}</p>
                  </div>
                  {(user?.id === comment.user_id || user?.role === 'admin') && (
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="text-red-400 hover:text-red-300 transition-colors ml-2"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="flex space-x-2">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
        />
        <button
          type="submit"
          disabled={submitting || !newComment.trim()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <FiSend size={18} />
        </button>
      </form>
    </div>
  );
};

export default TaskComments;
